/**
 * Spaces API client — maps Jira-style space/issue endpoints to
 * the goodday REST API (projects + tasks + statuses).
 * Base: /api/v1  (not /api/v1/goodday — projects/tasks live on the main router)
 */
import { graphqlRequest, PROJECT_QUERY, TASKS_BY_PROJECT_QUERY } from '../../lib/api-client'
import { getApiErrorMessage } from '../../shared/utils/apiError'
import {
  DEFAULT_STATUS_KEY,
  TASK_STATUSES,
  resolvePriority,
  resolveStatus,
  resolveTaskType,
  type TaskPriorityKey,
  type TaskTypeKey,
} from '@jira-clone/shared-types'
import { api } from '../../api/client'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000'

// Shares the main client's auth header and silent token refresh (see api/client.ts).
const v1 = api.raw

// ── Types ──────────────────────────────────────────────────────────────────

export interface SpaceDetail {
  id: string
  name: string
  key: string
  description?: string
  color?: string
  type?: string
  projectType?: string
  status?: string
  /** Departments the project belongs to (decides which department admins can see it). */
  departments?: string[]
  services?: string[]
  members: SpaceMember[]
  createdAt: string
  updatedAt: string
}

export interface SpaceMember {
  id: string
  userId: string
  name: string
  email: string
  avatarUrl?: string
  department?: string
  role: string
}

export interface Issue {
  id: string
  key: string
  title: string
  description?: string
  type: TaskTypeKey
  /** A status key from the fixed task configuration: 'backlog', 'todo', 'in_progress', 'in_review', 'done'. */
  status: string
  status_category: 'todo' | 'in_progress' | 'done'
  priority: TaskPriorityKey
  assignee_id?: string | null
  assigneeIds?: string[]
  reporter_id?: string | null
  parent_id?: string | null
  sprint_id?: string | null
  backlog_order?: number
  story_points?: number
  labels?: string[]
  due_date?: string | null
  created_at: string
  updated_at: string
  spaceId: string
  position?: number
  tags?: string[]
  estimatedHours?: number
  timeSpent?: number
  timeRemaining?: number | null
  startDate?: string | null
  start_date?: string | null
  team?: string
  department_id?: string | null
  customFieldValues?: Record<string, any>
  attachments?: IssueAttachment[]
  dependencies?: IssueDependency[]
  dependents?: IssueDependency[]
  flagged?: boolean
  flagged_by?: string | null
  flagged_at?: string | null
}

export interface IssueAttachment {
  id: string
  name: string
  url: string
  size?: number
  type?: string
  uploadedAt?: string
}

export interface IssueDependency {
  id: string
  sourceTaskId: string
  targetTaskId: string
  type: string
  lagDays?: number
  targetTask?: {
    id: string
    title: string
    number?: number
    statusId?: any
  }
  sourceTask?: {
    id: string
    title: string
    number?: number
    statusId?: any
  }
}

export interface WorkflowStatus {
  id: string
  name: string
  color: string
  category: 'todo' | 'in_progress' | 'done'
  position: number
}

export interface Sprint {
  id: string
  name: string
  goal?: string
  status: 'planning' | 'future' | 'active' | 'completed'
  startDate?: string | null
  endDate?: string | null
  start_date?: string | null
  end_date?: string | null
  spaceId?: string
}

// ── Mappers ────────────────────────────────────────────────────────────────

// Helper: extract raw ID string from a field that may be a populated Mongoose object or a raw string/ObjectId
function rawId(val: any): string | null {
  if (!val) return null
  if (typeof val === 'string') return val
  if (typeof val === 'object') return String(val._id ?? val.id ?? val)
  return String(val)
}

// GoodDayProjectMember docs come back nested: m.memberId (a populated
// GoodDayOrganizationMember) -> m.memberId.userId (a populated GoodDayUser),
// not the flat m.userId/m.name this used to assume - that older shape never
// actually matched what the backend sends, but it went unnoticed because
// GET /:id never returned a members array until the project-membership
// consolidation added one.
function mapProjectMember(m: any): SpaceMember {
  const orgMember = m.memberId ?? {}
  const user = orgMember.userId ?? {}
  return {
    id: rawId(m) ?? '',
    userId: rawId(user) ?? '',
    name: user.name ?? 'Unknown',
    email: user.email ?? '',
    avatarUrl: user.avatarUrl,
    department: user.department,
    role: m.accessLevel ?? orgMember.role ?? 'member',
  }
}

function mapProjectToSpace(p: any): SpaceDetail {
  return {
    id: p._id ?? p.id,
    name: p.name,
    key: p.key ?? p.name?.slice(0, 4).toUpperCase() ?? 'PROJ',
    description: p.description,
    color: p.color,
    type: p.type,
    projectType: p.projectType,
    status: p.status,
    departments: p.departments ?? [],
    services: p.services,
    members: (p.members ?? []).map(mapProjectMember),
    createdAt: p.createdAt,
    updatedAt: p.updatedAt,
  }
}

// The API expands a task's status/priority/type to objects ({id, name, ...});
// older payloads may carry just the key or a name. Everything is read through
// the fixed task configuration, so the UI only ever sees valid values.
const keyOrName = (v: any): unknown => (v && typeof v === 'object' ? (v.key ?? v.id ?? v._id ?? v.name ?? v.label) : v)

export function mapTaskToIssue(t: any): Issue {
  const status = resolveStatus(keyOrName(t.statusId) ?? keyOrName(t.status)) ?? resolveStatus(DEFAULT_STATUS_KEY)!
  const category: Issue['status_category'] =
    status.category === 'IN_PROGRESS' ? 'in_progress' : status.isCompleted || status.category === 'DONE' || status.category === 'CANCELLED' ? 'done' : 'todo'

  const priority = (resolvePriority(keyOrName(t.priorityId) ?? keyOrName(t.priority))?.key ?? 'medium') as TaskPriorityKey

  // A task with a parent is a subtask whatever its stored type says.
  const issueType: Issue['type'] = t.parentId ? 'subtask' : ((resolveTaskType(keyOrName(t.typeId) ?? keyOrName(t.type))?.key ?? 'task') as Issue['type'])

  const statusId = status.key

  // Resolve assignee — may be populated array of user objects or plain string array
  let assigneeId: string | null = null
  if (Array.isArray(t.assigneeIds) && t.assigneeIds.length > 0) {
    assigneeId = rawId(t.assigneeIds[0])
  } else if (t.assigneeId) {
    assigneeId = rawId(t.assigneeId)
  }

  // Resolve projectId (spaceId) — may be populated object
  const spaceId = rawId(t.projectId) ?? ''

  const rawSt = t.startDate ?? t.start_date
  const rawDue = t.dueDate ?? t.due_date
  const stDate = rawSt ? (rawSt instanceof Date ? rawSt.toISOString() : String(rawSt)) : null
  const dDate = rawDue ? (rawDue instanceof Date ? rawDue.toISOString() : String(rawDue)) : null
  const attachments = Array.isArray(t.attachments) ? t.attachments.map((a: any) => ({
    id: a.id ?? a._id?.toString() ?? `att-${Math.random().toString(36).slice(2, 7)}`,
    name: a.name || 'Attachment',
    url: a.url || '',
    size: a.size,
    type: a.type,
    uploadedAt: a.uploadedAt ? (a.uploadedAt instanceof Date ? a.uploadedAt.toISOString() : String(a.uploadedAt)) : undefined,
  })) : []

  const dependencies = Array.isArray(t.dependencies) ? t.dependencies.map((d: any) => ({
    id: d._id ?? d.id,
    sourceTaskId: rawId(d.sourceTaskId) ?? '',
    targetTaskId: rawId(d.targetTaskId) ?? '',
    type: d.type ?? 'FINISH_TO_START',
    targetTask: d.targetTaskId && typeof d.targetTaskId === 'object' ? {
      id: rawId(d.targetTaskId) ?? '',
      title: d.targetTaskId.title ?? 'Task',
      number: d.targetTaskId.number,
      statusId: d.targetTaskId.statusId,
    } : undefined,
  })) : []

  const dependents = Array.isArray(t.dependents) ? t.dependents.map((d: any) => ({
    id: d._id ?? d.id,
    sourceTaskId: rawId(d.sourceTaskId) ?? '',
    targetTaskId: rawId(d.targetTaskId) ?? '',
    type: d.type ?? 'FINISH_TO_START',
    sourceTask: d.sourceTaskId && typeof d.sourceTaskId === 'object' ? {
      id: rawId(d.sourceTaskId) ?? '',
      title: d.sourceTaskId.title ?? 'Task',
      number: d.sourceTaskId.number,
      statusId: d.sourceTaskId.statusId,
    } : undefined,
  })) : []

  const team = t.team || t.customFieldValues?.team || 'Design & Engineering'

  return {
    id: String(t._id ?? t.id ?? ''),
    key: t.key ?? `TASK-${t.number ?? String(t._id ?? t.id ?? '').slice(-4).toUpperCase()}`,
    title: t.title,
    description: t.description,
    type: issueType,
    status: statusId,
    status_category: category,
    priority,
    assignee_id: assigneeId,
    assigneeIds: Array.isArray(t.assigneeIds) && t.assigneeIds.length > 0 ? t.assigneeIds.map(rawId).filter(Boolean) : (assigneeId ? [assigneeId] : []),
    reporter_id: rawId(t.reporterId) ?? null,
    parent_id: rawId(t.parentId) ?? rawId(t.parent_id) ?? null,
    due_date: dDate,
    startDate: stDate,
    start_date: stDate,
    team,
    department_id: t.departmentId ? String(t.departmentId) : null,
    attachments,
    dependencies,
    dependents,
    created_at: t.createdAt ?? new Date().toISOString(),
    updated_at: t.updatedAt ?? t.createdAt ?? new Date().toISOString(),
    spaceId,
    position: t.position ?? 0,
    tags: t.tags ?? [],
    estimatedHours: t.estimatedHours,
    sprint_id: rawId(t.sprintId) ?? rawId(t.sprint_id) ?? (t.customFieldValues?.sprintId ? String(t.customFieldValues.sprintId) : null),
    backlog_order: t.backlogOrder ?? t.backlog_order ?? t.position ?? 0,
    story_points: t.storyPoints ?? t.story_points ?? t.estimatedHours ?? 0,
    timeSpent: t.timeSpent ?? t.loggedHours ?? 0,
    timeRemaining: t.timeRemaining ?? null,
    flagged: t.flagged ?? false,
    flagged_by: rawId(t.flaggedBy) ?? null,
    flagged_at: t.flaggedAt ? (t.flaggedAt instanceof Date ? t.flaggedAt.toISOString() : String(t.flaggedAt)) : null,
  }
}

const STATUS_CATEGORY: Record<string, WorkflowStatus['category']> = {
  TODO: 'todo',
  IN_PROGRESS: 'in_progress',
  DONE: 'done',
  CANCELLED: 'done',
}

// ── API Functions ──────────────────────────────────────────────────────────

/** List all spaces (projects) for the authenticated user's organization */
export async function listSpaces(): Promise<SpaceDetail[]> {
  try {
    const { data: orgsData } = await v1.get<any>('/organizations')
    const orgs = Array.isArray(orgsData) ? orgsData : (orgsData?.organizations ?? orgsData?.data ?? [])
    if (!orgs.length) return []
    const firstOrg = orgs[0]?.organization ?? orgs[0]
    const orgId = firstOrg?._id ?? firstOrg?.id
    if (!orgId) return []
    const { data: projData } = await v1.get<any>(`/projects?organizationId=${orgId}`)
    const projects = Array.isArray(projData) ? projData : (projData?.projects ?? projData?.data ?? [])
    return projects.map(mapProjectToSpace)
  } catch (err) {
    console.warn('REST listSpaces error, returning empty list:', err)
    return []
  }
}

/** Get single space (project) detail. Throws if the project can't be loaded. */
export async function getSpace(spaceId: string): Promise<SpaceDetail> {
  // First attempt: REST API (supports Prisma projects and Mongoose)
  let restError: unknown
  try {
    const { data } = await v1.get<any>(`/projects/${spaceId}`)
    const p = data.project ?? data
    if (p && p.name) {
      return mapProjectToSpace(p)
    }
  } catch (err) {
    restError = err
    // Fall through to GraphQL
  }

  // Second attempt: GraphQL (Prisma Project by CUID)
  try {
    const gqlData = await graphqlRequest<{ project: any }>(PROJECT_QUERY, { id: spaceId })
    if (gqlData?.project) {
      const p = gqlData.project
      return {
        id: p.id,
        name: p.name,
        key: p.key ?? p.name?.slice(0, 4).toUpperCase() ?? 'PROJ',
        description: p.description,
        color: p.color,
        type: p.type,
        departments: p.departments ?? [],
        services: p.services,
        members: (p.members ?? []).map((m: any) => ({
          id: m.id,
          userId: m.userId ?? m.id,
          name: m.name ?? 'Unknown',
          email: m.email ?? '',
          avatarUrl: m.avatarUrl,
          role: m.role ?? m.accessLevel ?? 'member',
        })),
        createdAt: p.createdAt ?? new Date().toISOString(),
        updatedAt: p.updatedAt ?? new Date().toISOString(),
      }
    }
  } catch {
    // Report the REST failure below - it's the more informative of the two.
  }

  // No placeholder space: pretending a project loaded (with a made-up name and
  // no members) let people create issues against a project that never did.
  throw restError ?? new Error('Space not found')
}

/** List issues (tasks) for a space (project) */
export async function listIssues(spaceId: string, opts?: { assigneeId?: string; onlyMine?: boolean }): Promise<Issue[]> {
  const queryParts = [`projectId=${spaceId}`]
  if (opts?.assigneeId) queryParts.push(`assigneeId=${opts.assigneeId}`)
  if (opts?.onlyMine) queryParts.push('mine=true')
  const queryString = queryParts.join('&')
  // First attempt: REST API
  try {
    const { data } = await v1.get<any>(`/tasks?${queryString}`)
    const tasks = Array.isArray(data) ? data : (data.tasks ?? data.data ?? [])
    if (tasks.length > 0) return tasks.map(mapTaskToIssue)
  } catch {
    // REST failed, try GraphQL
  }

  // Second attempt: GraphQL tasksByProject
  try {
    const gqlTasks = await graphqlRequest<{ tasksByProject: any[] }>(TASKS_BY_PROJECT_QUERY, { projectId: spaceId })
    if (gqlTasks?.tasksByProject && Array.isArray(gqlTasks.tasksByProject)) {
      return gqlTasks.tasksByProject.map(mapTaskToIssue)
    }
  } catch {
    // Fallback: return empty issue list
  }

  return []
}

/**
 * The statuses every space uses. They are the fixed task configuration
 * (@jira-clone/shared-types), the same for every project, so there is nothing
 * to fetch. Still async so callers (and the react-query hook) don't change.
 */
export async function listStatuses(_spaceId?: string): Promise<WorkflowStatus[]> {
  return TASK_STATUSES.map((s) => ({
    id: s.key,
    name: s.name,
    color: s.color,
    category: STATUS_CATEGORY[s.category] ?? 'todo',
    position: s.position,
  }))
}

/** List sprints for a space */
export async function listSprints(spaceId: string): Promise<Sprint[]> {
  try {
    const { data } = await v1.get<any>(`/projects/${spaceId}/sprints`)
    const sprints = Array.isArray(data) ? data : (data.sprints ?? data.data ?? [])
    return sprints.map((s: any) => ({
      id: s._id ?? s.id,
      name: s.name,
      goal: s.goal,
      status: s.status ?? 'planning',
      startDate: s.startDate ?? s.start_date,
      endDate: s.endDate ?? s.end_date,
      start_date: s.startDate ?? s.start_date,
      end_date: s.endDate ?? s.end_date,
    }))
  } catch {
    return []
  }
}

/** Create a sprint */
export async function createSprint(spaceId: string, payload: { name: string; goal?: string }): Promise<Sprint> {
  const { data } = await v1.post<any>(`/projects/${spaceId}/sprints`, payload, { skipErrorToast: true })
  return {
    id: data._id ?? data.id ?? `sprint-${Date.now()}`,
    name: data.name ?? payload.name,
    goal: data.goal ?? payload.goal,
    status: 'planning',
  }
}

/** Update sprint */
export async function updateSprint(
  sprintId: string,
  payload: { status?: 'planning' | 'future' | 'active' | 'completed'; name?: string; goal?: string },
): Promise<Sprint> {
  const { data } = await v1.patch<any>(`/sprints/${sprintId}`, payload, { skipErrorToast: true })
  return {
    id: data._id ?? data.id ?? sprintId,
    name: data.name ?? payload.name ?? 'Sprint',
    goal: data.goal ?? payload.goal,
    status: data.status ?? payload.status ?? 'planning',
  }
}

/** Delete a sprint */
export async function deleteSprint(sprintId: string): Promise<void> {
  await v1.delete(`/sprints/${sprintId}`, { skipErrorToast: true })
}

/** Create a new space (project) */
export async function createSpace(orgId: string, payload: { name: string; key: string; type?: string; description?: string; color?: string; departments?: string[] }): Promise<SpaceDetail> {
  const { data } = await v1.post<any>(`/projects`, { ...payload, organizationId: orgId })
  return mapProjectToSpace(data.project ?? data)
}

/** Delete (archive) a space */
export async function deleteSpace(spaceId: string): Promise<void> {
  await v1.delete(`/projects/${spaceId}`)
}

/** Update a space's metadata */
export async function updateSpace(spaceId: string, payload: {
  name?: string
  description?: string | null
  color?: string
  type?: string
  projectType?: string
  departments?: string[]
  services?: string[]
  status?: string
}): Promise<SpaceDetail> {
  const { data } = await v1.patch<any>(`/projects/${spaceId}`, payload)
  const p = data.project ?? data
  return mapProjectToSpace(p)
}

/** Create an issue (task) */
export async function createIssue(spaceId: string, payload: Partial<Issue> & { title: string }): Promise<Issue> {
  const { data } = await v1.post<any>(
    `/tasks`,
    {
      projectId: spaceId,
      title: payload.title,
      description: payload.description || undefined,
      statusId: payload.status,
      priorityId: payload.priority,
      typeId: payload.type,
      assigneeId: payload.assignee_id || undefined,
      assigneeIds: payload.assignee_id ? [payload.assignee_id] : [],
      reporterId: payload.reporter_id || undefined,
      dueDate: payload.due_date || undefined,
      parentId: payload.parent_id || undefined,
      tags: payload.tags,
      departmentId: payload.department_id || undefined,
    },
    { skipErrorToast: true }
  )
  return mapTaskToIssue(data.task ?? data)
}

/** @deprecated use getApiErrorMessage from shared/utils/apiError directly */
export const getIssueErrorMessage = getApiErrorMessage

export interface IssueComment {
  id: string
  content: string
  userId: string
  user?: {
    id: string
    name: string
    email?: string
    avatarUrl?: string
  }
  createdAt: string
  updatedAt: string
}

/** Get single issue details with comments and subtasks */
export async function getIssue(issueId: string): Promise<Issue & { comments?: IssueComment[]; subtasks?: Issue[] }> {
  try {
    const { data } = await v1.get<any>(`/tasks/${issueId}`)
    const task = data.task ?? data
    if (task && (task.id || task._id || task.title)) {
      const mapped = mapTaskToIssue(task)
      return {
        ...mapped,
        comments: (task.comments ?? []).map((c: any) => ({
          id: c._id ?? c.id,
          content: c.content,
          userId: c.userId?._id ?? c.userId?.id ?? c.userId,
          user: c.userId && typeof c.userId === 'object' ? {
            id: c.userId._id ?? c.userId.id,
            name: c.userId.name || 'User',
            email: c.userId.email,
            avatarUrl: c.userId.avatarUrl,
          } : undefined,
          createdAt: c.createdAt ?? new Date().toISOString(),
          updatedAt: c.updatedAt ?? new Date().toISOString(),
        })),
        subtasks: (task.subtasks ?? []).map(mapTaskToIssue),
      }
    }
  } catch {
    // REST error, proceed to fallback
  }

  return {
    id: issueId,
    key: `TASK-${issueId.slice(-4).toUpperCase()}`,
    title: 'Work Item',
    type: 'task',
    status: 'todo',
    status_category: 'todo',
    priority: 'medium',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    spaceId: '',
    comments: [],
    subtasks: [],
  }
}

/** List comments for an issue */
export async function listIssueComments(issueId: string): Promise<IssueComment[]> {
  try {
    const { data } = await v1.get<any>(`/tasks/${issueId}/comments`)
    const comments = Array.isArray(data) ? data : (data.comments ?? [])
    return comments.map((c: any) => ({
      id: c._id ?? c.id,
      content: c.content,
      userId: c.userId?._id ?? c.userId?.id ?? c.userId,
      user: c.userId && typeof c.userId === 'object' ? {
        id: c.userId._id ?? c.userId.id,
        name: c.userId.name || 'User',
        email: c.userId.email,
        avatarUrl: c.userId.avatarUrl,
      } : undefined,
      createdAt: c.createdAt ?? new Date().toISOString(),
      updatedAt: c.updatedAt ?? new Date().toISOString(),
    }))
  } catch {
    return []
  }
}

/** Add a comment to an issue */
export async function addIssueComment(issueId: string, content: string): Promise<IssueComment> {
  const { data } = await v1.post<any>(`/tasks/${issueId}/comments`, { content }, { skipErrorToast: true })
  const c = data.comment ?? data
  return {
    id: c._id ?? c.id,
    content: c.content,
    userId: c.userId?._id ?? c.userId?.id ?? c.userId,
    user: c.userId && typeof c.userId === 'object' ? {
      id: c.userId._id ?? c.userId.id,
      name: c.userId.name || 'User',
      email: c.userId.email,
      avatarUrl: c.userId.avatarUrl,
    } : undefined,
    createdAt: c.createdAt ?? new Date().toISOString(),
    updatedAt: c.updatedAt ?? new Date().toISOString(),
  }
}

/** Update an issue */
export async function updateIssue(
  issueId: string,
  payload: Partial<Issue> & {
    reporter_id?: string | null
    reporterId?: string | null
    startDate?: string | null
    start_date?: string | null
    team?: string
    customFieldValues?: any
    attachments?: IssueAttachment[]
  },
  // Kept so existing callers compile; type/priority no longer need the space
  // (they are fixed keys, not per-space rows).
  _spaceId?: string
): Promise<Issue> {
  const body: any = {}
  if (payload.title !== undefined) body.title = payload.title
  if (payload.description !== undefined) body.description = payload.description
  if (payload.status !== undefined) body.statusId = payload.status
  if (payload.priority !== undefined) body.priorityId = payload.priority
  if (payload.type !== undefined) body.typeId = payload.type
  if (payload.assignee_id !== undefined) {
    body.assigneeId = payload.assignee_id || null
    body.assigneeIds = payload.assignee_id ? [payload.assignee_id] : []
  }
  if (payload.reporter_id !== undefined || payload.reporterId !== undefined) {
    body.reporterId = payload.reporter_id ?? payload.reporterId ?? null
  }
  if (payload.due_date !== undefined) {
    body.dueDate = payload.due_date ? new Date(payload.due_date).toISOString() : null
  }
  if (payload.start_date !== undefined || payload.startDate !== undefined) {
    const sDate = payload.start_date ?? payload.startDate
    body.startDate = sDate ? new Date(sDate).toISOString() : null
  }
  if (payload.team !== undefined) {
    body.team = payload.team
    body.customFieldValues = { ...(payload.customFieldValues || {}), team: payload.team }
  } else if (payload.customFieldValues !== undefined) {
    body.customFieldValues = payload.customFieldValues
  }
  if (payload.parent_id !== undefined) body.parentId = payload.parent_id
  if (payload.position !== undefined) body.position = payload.position
  if (payload.story_points !== undefined) body.estimatedHours = payload.story_points
  if (payload.estimatedHours !== undefined) body.estimatedHours = payload.estimatedHours
  if (payload.tags !== undefined) body.tags = payload.tags
  if (payload.labels !== undefined) body.tags = payload.labels
  if (payload.attachments !== undefined) body.attachments = payload.attachments
  if (payload.sprint_id !== undefined) {
    body.sprintId = payload.sprint_id || null
    body.customFieldValues = { ...(body.customFieldValues || {}), sprintId: payload.sprint_id || null }
  }
  if (payload.backlog_order !== undefined) {
    body.position = payload.backlog_order
  }
  if (payload.timeSpent !== undefined) body.timeSpent = payload.timeSpent
  if (payload.timeRemaining !== undefined) body.timeRemaining = payload.timeRemaining
  if (payload.department_id !== undefined) body.departmentId = payload.department_id || null

  const { data } = await v1.patch<any>(`/tasks/${issueId}`, body, { skipErrorToast: true })
  return mapTaskToIssue(data.task ?? data)
}

/** Flag/unflag an issue */
export async function flagIssue(issueId: string, flag: boolean): Promise<Issue> {
  const { data } = await v1.patch<any>(`/tasks/${issueId}/flag`, { flag }, { skipErrorToast: true })
  return mapTaskToIssue(data.task ?? data)
}

/** Add attachment to issue */
export async function addIssueAttachment(
  issueId: string,
  attachment: { name: string; url: string; size?: number; type?: string }
): Promise<IssueAttachment> {
  const { data } = await v1.post<any>(`/tasks/${issueId}/attachments`, attachment, { skipErrorToast: true })
  return data.attachment ?? data
}

/** Delete attachment from issue */
export async function deleteIssueAttachment(issueId: string, attachmentId: string): Promise<void> {
  await v1.delete(`/tasks/${issueId}/attachments/${attachmentId}`, { skipErrorToast: true })
}

/** Create task dependency / link */
export async function createTaskDependency(
  sourceTaskId: string,
  targetTaskId: string,
  type = 'FINISH_TO_START'
): Promise<IssueDependency> {
  const { data } = await v1.post<any>(`/tasks/dependencies`, { sourceTaskId, targetTaskId, type }, { skipErrorToast: true })
  return data
}

/** Delete task dependency / link */
export async function deleteTaskDependency(dependencyId: string): Promise<void> {
  await v1.delete(`/tasks/dependencies/${dependencyId}`, { skipErrorToast: true })
}

/** Delete an issue */
export async function deleteIssue(issueId: string): Promise<void> {
  await v1.delete(`/tasks/${issueId}`, { skipErrorToast: true })
}

/** List members of a space */
export async function listSpaceMembers(spaceId: string): Promise<SpaceMember[]> {
  try {
    const { data } = await v1.get<any>(`/projects/${spaceId}/members`)
    const members = Array.isArray(data) ? data : (data.members ?? data.data ?? [])
    return members.map(mapProjectMember)
  } catch {
    return []
  }
}

/** Save assigned member IDs for a space project to backend */
export async function saveSpaceMembers(spaceId: string, memberIds: string[]): Promise<void> {
  await v1.put<any>(`/projects/${spaceId}/members`, { memberIds }, { skipErrorToast: true })
}

/** Add a single member to a space project */
export async function addSpaceMember(spaceId: string, userId: string, accessLevel: string = 'EDITOR'): Promise<void> {
  await v1.post<any>(`/projects/${spaceId}/members`, { userId, accessLevel }, { skipErrorToast: true })
}

/** Remove a single member from a space project */
export async function removeSpaceMember(spaceId: string, userId: string): Promise<void> {
  const current = await listSpaceMembers(spaceId)
  const memberIds = current
    .map((m) => m.userId)
    .filter((id) => id !== userId)
  await saveSpaceMembers(spaceId, memberIds)
}

/** Create a new user in the current organization */
export interface CreateUserInput {
  email: string
  name: string
  username?: string
  employeeId?: string
  department: string
  departmentRole?: string
  role?: string
  password?: string
}

export async function createUser(input: CreateUserInput): Promise<any> {
  const { data } = await v1.post<any>('/users', input, { skipErrorToast: true })
  return data
}

// ── Watchers ─────────────────────────────────────────────────────────────────

export async function listIssueWatchers(issueId: string): Promise<
  Array<{ id: string; userId: string; user?: { id: string; name: string; email: string } }>
> {
  const { data } = await v1.get<any>(`/tasks/${issueId}/watchers`)
  return (data.watchers || []).map((w: any) => ({
    id: w.id,
    userId: w.userId,
    user: w.user,
  }))
}

export async function addIssueWatcher(issueId: string, userId: string): Promise<any> {
  const { data } = await v1.post<any>(`/tasks/${issueId}/watchers`, { userId }, { skipErrorToast: true })
  return data.watcher
}

export async function removeIssueWatcher(issueId: string, watcherId: string): Promise<void> {
  await v1.delete(`/tasks/${issueId}/watchers/${watcherId}`, { skipErrorToast: true })
}

// ── Worklog ──────────────────────────────────────────────────────────────────

export interface WorklogEntry {
  id: string
  hours: number
  comment?: string
  date?: string
  user?: { id: string; name: string; email: string }
}

export async function listIssueWorklog(issueId: string): Promise<WorklogEntry[]> {
  const { data } = await v1.get<any>(`/tasks/${issueId}/worklog`)
  return (data.worklog || []).map((w: any) => ({
    id: w.id,
    hours: w.hours,
    comment: w.comment,
    date: w.date,
    user: w.user,
  }))
}

export async function addIssueWorklog(issueId: string, payload: { hours: number; comment?: string; startedAt?: string }): Promise<WorklogEntry> {
  const { data } = await v1.post<any>(`/tasks/${issueId}/worklog`, payload, { skipErrorToast: true })
  const w = data.worklog
  return {
    id: w.id,
    hours: w.hours,
    comment: w.comment,
    date: w.date,
    user: w.user,
  }
}

export interface UpdateTimeTrackingPayload {
  timeSpent: number
  timeRemaining: number | null
}

export async function updateIssueTimeTracking(issueId: string, payload: UpdateTimeTrackingPayload): Promise<any> {
  const { data } = await v1.patch<any>(`/tasks/${issueId}/time-tracking`, payload, { skipErrorToast: true })
  return data
}

// ── History ──────────────────────────────────────────────────────────────────

export interface TaskHistoryEntry {
  id: string
  field: string
  oldValue?: string | null
  newValue?: string | null
  user?: { id: string; name: string; email: string }
  createdAt?: string
}

export async function listIssueHistory(issueId: string): Promise<TaskHistoryEntry[]> {
  const { data } = await v1.get<any>(`/tasks/${issueId}/history`)
  return (data.history || []).map((h: any) => ({
    id: h.id,
    field: h.field,
    oldValue: h.oldValue,
    newValue: h.newValue,
    user: h.user,
    createdAt: h.createdAt,
  }))
}

export async function createCustomField(payload: {
  name: string
  type: string
  projectId?: string
  organizationId: string
  config?: Record<string, any>
  isRequired?: boolean
}): Promise<any> {
  const { data } = await api.post<any>('/goodday/custom-fields', payload)
  return data
}

export async function deleteCustomField(fieldId: string): Promise<void> {
  await api.delete(`/goodday/custom-fields/${fieldId}`)
}
